// src/pages/ProductDetail.js
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { getProduct, getReviews, addReview } from "../utils/firestoreService";
import toast from "react-hot-toast";

export default function ProductDetail() {
  const { id } = useParams();
  const { dispatch } = useCart();
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState({ name: "", rating: 5, comment: "" });
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState("desc");

  useEffect(() => {
    setLoading(true);
    Promise.all([getProduct(id), getReviews(id)])
      .then(([p, r]) => {
        setProduct(p);
        setReviews(r);
        // Preseleccionar primera variante con stock disponible
        if (p?.variantStock?.length > 0) {
          const firstAvailable = p.variantStock.find(v => v.stock > 0);
          setSelectedVariant(firstAvailable || p.variantStock[0]);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!product) return (
    <div className="empty-state page-pad">
      <h2>Producto no encontrado</h2>
      <Link to="/tienda" className="btn-primary">Volver a la tienda</Link>
    </div>
  );

  const formatPrice = p =>
    new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 }).format(p);

  const hasVariants = product.variantStock && product.variantStock.length > 0;

  // Stock disponible según variante seleccionada o stock total si no hay variantes
  const stockDisponible = hasVariants
    ? (selectedVariant?.stock || 0)
    : (product.stock || 0);

  const handleAdd = () => {
    if (hasVariants && !selectedVariant) {
      toast.error("Selecciona una opción antes de agregar al carrito");
      return;
    }
    if (stockDisponible === 0) {
      toast.error("Esta variante no tiene stock disponible");
      return;
    }
    const cartItem = {
      ...product,
      id: selectedVariant ? `${product.id}_${selectedVariant.name}` : product.id,
      productId: product.id,
      selectedVariant: selectedVariant?.name || null,
      name: selectedVariant ? `${product.name} — ${selectedVariant.name}` : product.name
    };
    for (let i = 0; i < qty; i++) dispatch({ type: "ADD_ITEM", payload: cartItem });
    toast.success(
      selectedVariant
        ? `"${selectedVariant.name}" añadido al carrito ✓`
        : `${qty} unidad${qty > 1 ? "es" : ""} añadida${qty > 1 ? "s" : ""} al carrito ✓`
    );
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.comment) return toast.error("Completa todos los campos");
    setSubmitting(true);
    await addReview(id, reviewForm);
    const updated = await getReviews(id);
    setReviews(updated);
    setReviewForm({ name: "", rating: 5, comment: "" });
    toast.success("¡Gracias por tu reseña!");
    setSubmitting(false);
  };

  return (
    <div className="product-detail page-pad">
      <nav className="breadcrumb">
        <Link to="/">Inicio</Link> / <Link to="/tienda">Tienda</Link> / <span>{product.name}</span>
      </nav>

      <div className="detail-grid">
        {/* GALERÍA */}
        <div className="detail-gallery">
          <div className="main-img-wrap">
            <img src={product.images?.[imgIdx] || "/placeholder.jpg"} alt={product.name} className="main-img" />
            {product.badge && <span className="badge">{product.badge}</span>}
          </div>
          {product.images?.length > 1 && (
            <div className="thumb-strip">
              {product.images.map((img, i) => (
                <button key={i} className={`thumb ${i === imgIdx ? "active" : ""}`} onClick={() => setImgIdx(i)}>
                  <img src={img} alt={`Vista ${i + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="detail-info">
          <span className="detail-category">{product.category}</span>
          <h1 className="detail-title">{product.name}</h1>

          {reviews.length > 0 && (
            <div className="detail-rating">
              {"★".repeat(Math.round(product.rating || 0))}{"☆".repeat(5 - Math.round(product.rating || 0))}
              <span>({reviews.length} reseña{reviews.length !== 1 ? "s" : ""})</span>
            </div>
          )}

          <div className="detail-price">
            {product.originalPrice && <span className="original">{formatPrice(product.originalPrice)}</span>}
            <span className="current">{formatPrice(product.price)}</span>
            {product.originalPrice && (
              <span className="discount-pct">-{Math.round((1 - product.price / product.originalPrice) * 100)}%</span>
            )}
          </div>

          <p className="detail-short-desc">{product.shortDesc}</p>

          {/* VARIANTES CON STOCK */}
          {hasVariants && (
            <div className="variants">
              <label className="variants-label">
                Opción:{" "}
                {selectedVariant && (
                  <strong>
                    {selectedVariant.name}
                    {selectedVariant.stock > 0
                      ? selectedVariant.stock <= 5
                        ? ` · ¡Solo ${selectedVariant.stock} disponibles!`
                        : ""
                      : " · Agotado"}
                  </strong>
                )}
              </label>
              <div className="variant-chips">
                {product.variantStock.map((v, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`variant-chip ${selectedVariant?.name === v.name ? "selected" : ""} ${v.stock === 0 ? "variant-agotada" : ""}`}
                    onClick={() => { setSelectedVariant(v); setQty(1); }}
                    title={v.stock === 0 ? "Agotado" : `${v.stock} disponibles`}
                  >
                    {v.name}
                    {v.stock === 0 && <span className="chip-agotado"> ✕</span>}
                  </button>
                ))}
              </div>
              {selectedVariant && selectedVariant.stock === 0 && (
                <span className="variant-hint">⚠️ Esta opción está agotada. Selecciona otra.</span>
              )}
            </div>
          )}

          {/* CANTIDAD */}
          <div className="qty-row">
            <label>Cantidad:</label>
            <div className="qty-control">
              <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
              <span>{qty}</span>
              <button onClick={() => setQty(q => Math.min(stockDisponible || 99, q + 1))}>+</button>
            </div>
            {stockDisponible > 0 && stockDisponible <= 5 && (
              <span className="low-stock">¡Solo quedan {stockDisponible}!</span>
            )}
          </div>

          <div className="detail-actions">
            <button
              className="btn-primary btn-full"
              onClick={handleAdd}
              disabled={stockDisponible === 0 || (hasVariants && !selectedVariant)}
            >
              {stockDisponible === 0
                ? "Agotado"
                : hasVariants && !selectedVariant
                ? "Selecciona una opción"
                : "Añadir al carrito"}
            </button>
            <a
              href={`https://wa.me/573000000000?text=Hola!%20quiero%20info%20sobre%20${encodeURIComponent(product.name)}${selectedVariant ? `%20(${encodeURIComponent(selectedVariant.name)})` : ""}`}
              target="_blank" rel="noreferrer"
              className="btn-whatsapp-sm"
            >
              Consultar por WhatsApp
            </a>
          </div>

          <div className="detail-badges">
            <span>🚚 Envío a todo Colombia</span>
            <span>🔒 Pago seguro</span>
            <span>🎁 Empaque especial</span>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="product-tabs">
        <div className="tab-nav">
          <button className={tab === "desc" ? "active" : ""} onClick={() => setTab("desc")}>Descripción</button>
          <button className={tab === "specs" ? "active" : ""} onClick={() => setTab("specs")}>Especificaciones</button>
          <button className={tab === "reviews" ? "active" : ""} onClick={() => setTab("reviews")}>Reseñas ({reviews.length})</button>
        </div>
        <div className="tab-content">
          {tab === "desc" && (
            <div className="tab-desc" dangerouslySetInnerHTML={{ __html: product.description || product.shortDesc }} />
          )}
          {tab === "specs" && (
            <div className="tab-specs">
              {product.specs && Object.keys(product.specs).length > 0 ? (
                <dl>{Object.entries(product.specs).map(([k,v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
              ) : <p>Material: PLA ecológico · Impresión: FDM 3D · Hecho en Colombia</p>}
            </div>
          )}
          {tab === "reviews" && (
            <div className="tab-reviews">
              {reviews.length === 0
                ? <p className="no-reviews">Sé el primero en reseñar este producto.</p>
                : <div className="reviews-list">
                    {reviews.map(r => (
                      <div key={r.id} className="review-item">
                        <div className="review-header">
                          <strong>{r.name}</strong>
                          <span className="review-stars">{"★".repeat(r.rating)}</span>
                        </div>
                        <p>{r.comment}</p>
                      </div>
                    ))}
                  </div>
              }
              <form className="review-form" onSubmit={handleReview}>
                <h4>Deja tu reseña</h4>
                <input type="text" placeholder="Tu nombre" value={reviewForm.name} onChange={e => setReviewForm(f => ({...f, name: e.target.value}))} />
                <div className="star-select">
                  {[1,2,3,4,5].map(n => (
                    <button type="button" key={n} className={n <= reviewForm.rating ? "star active" : "star"} onClick={() => setReviewForm(f => ({...f, rating: n}))}>★</button>
                  ))}
                </div>
                <textarea placeholder="Cuéntanos tu experiencia..." value={reviewForm.comment} onChange={e => setReviewForm(f => ({...f, comment: e.target.value}))} rows={4} />
                <button type="submit" className="btn-primary" disabled={submitting}>{submitting ? "Enviando..." : "Enviar reseña"}</button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
